import { Col, Input, message, notification, Row, Select, Space, Spin, Table, Tag } from 'antd'
import {
  Button,
  ButtonActionBar,
  DeleteAndUpdate,
  DropdownMenu,
  EmptyScreen,
  InputCell,
} from 'modules/core/components'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import configuration from 'modules/configuration'
import { SapCompanyCreateModal } from './components'
import { useTranslation } from 'react-i18next'
import { EditOutlined } from '@ant-design/icons'
import { generateMessageError } from 'modules/core/utils'
import { usePagination } from 'modules/core/customHooks'

const SapCompaniesList = ({
  fetchSapCompanies,
  sapCompaniesList,
  editSapCompany,
  fetchAllDimensions,
  fetchAllDimensionsValues,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [dataCellEdit, setDataCellEdit] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [tablePagination, setTablePagination] = usePagination()
  const [isLoading, setIsLoading] = useState(false)
  const [allDimensionsValuesCompany, setAllDimensionsValuesCompany] = useState([])
  const { t } = useTranslation()

  useEffect(() => {
    setIsLoading(true)
    fetchSapCompanies({ ...tablePagination }).then(() => setIsLoading(false))
  }, [fetchSapCompanies, tablePagination])

  useEffect(() => {
    fetchAllDimensions().then((response) => {
      const idCompania = response.payload.data.find((el) => el.name === 'sociedad')?.id
      if (idCompania) {
        fetchAllDimensionsValues({ id_dimension: idCompania }).then((res) => {
          setAllDimensionsValuesCompany(res.payload.data)
        })
      }
    })
  }, [fetchAllDimensions, fetchAllDimensionsValues])

  const dataSource = useMemo(
    () => (Array.isArray(sapCompaniesList) ? sapCompaniesList : sapCompaniesList?.results || []),
    [sapCompaniesList],
  )

  const companyMap = useMemo(() => {
    const map = {}
    allDimensionsValuesCompany.forEach((d) => {
      map[d.code_destination] = `${d.code_destination} - ${d.name_destination}`
    })
    return map
  }, [allDimensionsValuesCompany])

  const activeOptions = useMemo(
    () => [
      { value: true, label: t('CONFIG_SAP_COMPANY_FIELD_ACTIVE_TRUE') },
      { value: false, label: t('CONFIG_SAP_COMPANY_FIELD_ACTIVE_FALSE') },
    ],
    [t],
  )

  const onCellEdit = (name, value) => {
    setDataCellEdit({ ...dataCellEdit, [`${name}`]: value })
  }

  const onConfirm = () => {
    setEditRow(null)
    setDataCellEdit({})
  }

  const onCancel = () => setEditRow(null)

  const handleEdit = (id) => {
    setIsSaving(true)
    const original = dataSource.find((item) => item.id === id)
    const { id: _id, updated_at: _updatedAt, ...rest } = original
    const payload = { ...rest, ...dataCellEdit }
    editSapCompany(id, payload)
      .then(() =>
        fetchSapCompanies({ ...tablePagination }).then(() => {
          message.success(t('FEEDBACK_CHANGES_SAVED_SUCCESS'), 8)
          setIsSaving(false)
          setDataCellEdit({})
          setEditRow(null)
        }),
      )
      .catch((error) => {
        setIsSaving(false)
        setDataCellEdit({})
        setEditRow(null)
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
      })
  }

  const columns = [
    {
      dataIndex: 'company_code',
      title: t('CONFIG_SAP_COMPANY_FIELD_COMPANY_CODE'),
      render: (value) => companyMap[value] || value,
    },
    {
      dataIndex: 'company_db',
      title: t('CONFIG_SAP_COMPANY_FIELD_COMPANY_DB'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return <InputCell initialValue={value} name="company_db" onCellUpdate={onCellEdit} />
        }
        return value
      },
    },
    {
      dataIndex: 'user_name',
      title: t('CONFIG_SAP_COMPANY_FIELD_USER_NAME'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return <InputCell initialValue={value} name="user_name" onCellUpdate={onCellEdit} />
        }
        return value
      },
    },
    {
      dataIndex: 'password',
      title: t('CONFIG_SAP_COMPANY_FIELD_PASSWORD'),
      render: (_, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Input.Password
              autoComplete="new-password"
              placeholder={t('CONFIG_SAP_COMPANY_FIELD_PASSWORD_KEEP')}
              onChange={(e) => onCellEdit('password', e.target.value)}
              style={{ width: 200 }}
            />
          )
        }
        return '••••••••'
      },
    },
    {
      dataIndex: 'serie',
      title: t('CONFIG_SAP_COMPANY_FIELD_SERIE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <InputCell
              initialValue={value}
              name="serie"
              type="number"
              style={{ width: 100 }}
              onCellUpdate={onCellEdit}
            />
          )
        }
        return value
      },
    },
    {
      dataIndex: 'active',
      title: t('CONFIG_SAP_COMPANY_FIELD_ACTIVE'),
      render: (value, row) => {
        if (editRow && row.id === editRow) {
          return (
            <Select
              defaultValue={value}
              options={activeOptions}
              onChange={(val) => onCellEdit('active', val)}
              style={{ width: '100%' }}
            />
          )
        }
        return (
          <Tag color={value ? 'green' : 'red'}>
            {value
              ? t('CONFIG_SAP_COMPANY_FIELD_ACTIVE_TRUE')
              : t('CONFIG_SAP_COMPANY_FIELD_ACTIVE_FALSE')}
          </Tag>
        )
      },
    },
    {
      dataIndex: 'id',
      title: t('FIELD_ACTIONS'),
      align: 'right',
      render: (id) => {
        return id !== editRow ? (
          <Space size="middle">
            <DropdownMenu
              title={t('ACTION_MORE')}
              menu={[
                {
                  title: t('ACTION_EDIT'),
                  icon: <EditOutlined />,
                  onClick: () => setEditRow(id),
                },
              ]}
            />
          </Space>
        ) : (
          <DeleteAndUpdate
            onCancel={onCancel}
            onConfirm={onConfirm}
            onSave={() => handleEdit(id)}
            dataCellEdit={dataCellEdit}
            loading={isSaving}
          />
        )
      },
    },
  ]

  return (
    <Spin spinning={isLoading}>
      {dataSource.length === 0 && !isLoading && (
        <EmptyScreen
          image={emptyStateImage}
          description={t('CONFIG_SAP_COMPANY_EMPTY_DESCRIPTION')}
          footer={
            <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
          }
        />
      )}
      {dataSource.length > 0 && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <ButtonActionBar>
              <Button.Primary title="ACTION_CREATE_NEW" onClick={() => setShowCreateModal(true)} />
            </ButtonActionBar>
          </Col>
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={dataSource}
              rowKey="id"
              loading={isSaving}
              onChange={setTablePagination}
              pagination={{
                pageSize: tablePagination.page_size,
                current: tablePagination.page,
                total: sapCompaniesList?.count ?? dataSource.length,
                size: 'small',
                pageSizeOptions: ['10', '20', '30'],
                showSizeChanger: true,
              }}
            />
          </Col>
        </Row>
      )}
      <SapCompanyCreateModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onRefetch={() => fetchSapCompanies({ ...tablePagination })}
        allDimensionsValuesCompany={allDimensionsValuesCompany}
      />
    </Spin>
  )
}

const mapStateToProps = (state) => ({
  sapCompaniesList: configuration.selectors.getSapCompaniesList(state),
})

const mapDispatchToProps = {
  fetchSapCompanies: configuration.actions.fetchSapCompanies,
  editSapCompany: configuration.actions.editSapCompany,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
}

export default connect(mapStateToProps, mapDispatchToProps)(SapCompaniesList)
